import React, { useRef, useState, useEffect } from 'react';
import { useGetDimensions } from 'react-hooks-get-dimensions';

import { Box } from '@mui/system';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Card,
  Alert,
  Skeleton,
  Accordion,
  Typography,
  AccordionDetails,
  AccordionSummary,
} from '@mui/material';

import useLocales from '../../hooks/useLocales';
import { useSelector } from '../../redux/store';
import { getFileType } from '../../utils/getFileFormat';
import { UploadMultiFile } from '../../components/upload';
import { getBase64FromFile } from '../../utils/fileUtils';
import FormConstants from '../forms/filledForm.constants';
import useNotifications from '../../hooks/useNotifications';
import documentService from '../../services/documentService';
import { getHistory } from '../../redux/slices/documentHistory';
import { FileExtensions } from '../../@types/fileView/enums/fileExtensions';
import { DocumentHistoryFilter } from '../../@types/documents/documentHistoryFilter';
import DocumentAccompanyingDocumentElement from './DocumentAccompanyingDocumentElement';
import { SKELETON_BGCOLOR, SKELETON_ANIMATION } from '../../utils/constants/dlxDriveConstants';
import DocumentAccompanyingDocumentElementSkeleton from './DocumentAccompanyingDocumentElementSkeleton';
import {
  AccompanyingDocumentProps,
  AccompanyingDocumentUpload,
  AccompanyingDocumentFileProps,
} from '../../@types/documents/types';

// ----------------------------------------------------------------------

const DocumentAccompanyingDocuments: React.FC = () => {
  const { t } = useLocales();
  const { document } = useSelector((state) => state.document);
  const [uploadedDocuments, setUploadedDocuments] = useState<File[]>([]);
  const [accompanyingDocuments, setAccompanyingDocuments] = useState<AccompanyingDocumentProps[]>(
    []
  );
  const [maxFilesError, setMaxFilesError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { enqueueSuccess, enqueueError } = useNotifications();
  const itemWrapperWidth = useRef();
  const { width } = useGetDimensions(itemWrapperWidth);

  const AVAILABLE_EXTENSIONS: string[] = [
    FileExtensions.Jpeg,
    FileExtensions.Jpg,
    FileExtensions.Png,
    FileExtensions.Pdf,
    FileExtensions.Doc,
    FileExtensions.Docx,
    FileExtensions.Xls,
    FileExtensions.Xlsx,
  ];

  const handleHistoryUpdate = async () => {
    const historyFilter: DocumentHistoryFilter = {
      id: document ? document.id : 0,
      searchQuery: '',
      offset: 0,
      limit: 5,
    };

    await getHistory(historyFilter);
  };

  const handleDrop = (acceptedFiles: File[]) => {
    const newAttachments = [...uploadedDocuments, ...acceptedFiles];
    setMaxFilesError(newAttachments.length > FormConstants.MAX_FILES);
    setUploadedDocuments(newAttachments.slice(0, FormConstants.MAX_FILES));
  };

  const docTypeValidator = (file: File) => {
    const extension = getFileType(file.name);
    if (!AVAILABLE_EXTENSIONS.includes(extension)) {
      return {
        code: 'file-invalid-type',
        message: 'components.uploadSingleFile.fileMustBe',
        parameters: { data: AVAILABLE_EXTENSIONS.join(', ') },
      };
    }

    return null;
  };

  const getAccompanyingDocumentsList = async () => {
    const documentId = document?.id;

    if (documentId) {
      try {
        setIsLoading(true);

        const list = await documentService.getAccompanyingDocuments(documentId);
        setAccompanyingDocuments(list);
      } catch (error) {
        enqueueError(error.message);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const uploadAccompanyingDocuments = async (model: AccompanyingDocumentUpload) => {
    try {
      setIsLoading(true);

      await documentService.setAccompanyingDocuments(model);

      enqueueSuccess(
        t('documents.accompanyingDocumentSuccessfullyUploaded', { docCount: model.files.length })
      );
    } catch (error) {
      enqueueError(error.message);
    } finally {
      setUploadedDocuments([]);
      await getAccompanyingDocumentsList();

      setIsLoading(false);
    }
  };

  const createAccompanyingDocumentsUploadModel = async (documentId: number) => {
    const model: AccompanyingDocumentUpload = {
      documentId,
      files: await Promise.all(
        uploadedDocuments.map(
          async (a) =>
            ({
              name: a.name,
              extension: getFileType(a.name),
              file: await getBase64FromFile(a),
            }) as AccompanyingDocumentFileProps
        )
      ),
    };

    try {
      await uploadAccompanyingDocuments(model);
    } catch (error) {
      enqueueError(error.message);
    } finally {
      await handleHistoryUpdate();
    }
  };

  useEffect(() => {
    const documentId = document?.id;

    if (uploadedDocuments.length > 0 && documentId) {
      createAccompanyingDocumentsUploadModel(documentId).then();
    }
  }, [uploadedDocuments]);

  useEffect(() => {
    if (document) {
      getAccompanyingDocumentsList().then();
    }
  }, [document]);

  return (
    <Card>
      <Accordion defaultExpanded sx={{ p: 3 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ p: 3 }}>
          <Typography variant="h6"> {t('documents.accompanyingDocuments')}</Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 3, pt: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {isLoading && (
              <Box
                sx={(theme) => ({
                  outline: 'none',
                  display: 'flex',
                  textAlign: 'center',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: theme.spacing(2, 0),
                  borderRadius: '16px',
                  backgroundColor: theme.palette.background.paper,
                  border: `1px dashed ${theme.palette.grey[500_32]}`,
                  '&:hover': { opacity: 0.72, cursor: 'pointer' },
                  p: 2,
                })}
              >
                <Box
                  sx={{
                    width: '100%',
                    maxWidth: '350px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                  }}
                >
                  <Typography component="div" variant="h5" sx={{ width: '50%' }}>
                    <Skeleton
                      animation={SKELETON_ANIMATION}
                      sx={{ backgroundColor: SKELETON_BGCOLOR }}
                    />
                  </Typography>

                  <Typography component="div" variant="h5" sx={{ width: '100%' }}>
                    <Skeleton
                      animation={SKELETON_ANIMATION}
                      sx={{ backgroundColor: SKELETON_BGCOLOR }}
                    />
                  </Typography>

                  <Typography component="div" variant="h5" sx={{ width: '100%' }}>
                    <Skeleton
                      animation={SKELETON_ANIMATION}
                      sx={{ backgroundColor: SKELETON_BGCOLOR }}
                    />
                  </Typography>
                </Box>

                <Skeleton
                  sx={{ backgroundColor: SKELETON_BGCOLOR, borderRadius: '4px' }}
                  animation={SKELETON_ANIMATION}
                  variant="rectangular"
                  width={140}
                  height={116}
                />
              </Box>
            )}

            {!isLoading && (
              <>
                <UploadMultiFile
                  showRejections
                  isCompact
                  accept={AVAILABLE_EXTENSIONS.map((x) => `.${x}`).join(', ')}
                  onDrop={handleDrop}
                  validator={docTypeValidator}
                  isRowDirection
                />

                {maxFilesError && (
                  <Alert severity="error">
                    <Typography variant="subtitle2">
                      {t('documents.errors.maximumFiles', { max: FormConstants.MAX_FILES })}
                    </Typography>
                  </Alert>
                )}
              </>
            )}

            {isLoading && accompanyingDocuments.length >= 2 && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {accompanyingDocuments.map((_, index) => (
                  <DocumentAccompanyingDocumentElementSkeleton key={`element-skeleton-${index}`} />
                ))}
              </Box>
            )}

            {isLoading && accompanyingDocuments.length < 2 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <DocumentAccompanyingDocumentElementSkeleton />
                <DocumentAccompanyingDocumentElementSkeleton />
              </Box>
            )}

            {!isLoading && (
              <Box ref={itemWrapperWidth} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {accompanyingDocuments.length > 0 &&
                  accompanyingDocuments.map((accompanyingDocument, index) => (
                    <DocumentAccompanyingDocumentElement
                      key={`accompanying-document-element-${index}`}
                      accompanyingDocument={accompanyingDocument}
                      getAccompanyingDocumentsList={getAccompanyingDocumentsList}
                      setIsLoading={setIsLoading}
                      wrapperWidth={width}
                      handleHistoryUpdate={handleHistoryUpdate}
                    />
                  ))}
              </Box>
            )}
          </Box>
        </AccordionDetails>
      </Accordion>
    </Card>
  );
};

export default DocumentAccompanyingDocuments;
